import React, { useState, createContext, useContext } from 'react';

interface TabContextType {
  activeTab: string;
  setActiveTab: (id: string) => void;
}

const TabContext = createContext<TabContextType | undefined>(undefined);

export interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Default active tab ID */
  defaultActiveTab?: string;
  /** Callback when active tab changes */
  onTabChange?: (tabId: string) => void;
  /** Variant of tabs */
  variant?: 'default' | 'pills' | 'underline';
  /** Allow dynamic content height */
  dynamicHeight?: boolean;
  /** Tab position */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Whether the tabs are full width */
  fullWidth?: boolean;
  /** Size of tabs */
  size?: 'sm' | 'md' | 'lg';
}

export interface TabListProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Whether tabs can be navigated with arrow keys */
  ariaKeyboardNav?: boolean;
  /** Position passed from parent Tabs */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Variant passed from parent Tabs */
  variant?: 'default' | 'pills' | 'underline';
  /** Full width passed from parent Tabs */
  fullWidth?: boolean;
  /** Size passed from parent Tabs */
  size?: 'sm' | 'md' | 'lg';
}

export interface TabProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Unique ID for the tab */
  tabId: string;
  /** Whether the tab is disabled */
  disabled?: boolean;
  /** Icon to display before the tab label */
  icon?: React.ReactNode;
  /** Badge or notification indicator */
  badge?: React.ReactNode;
  /** Variant passed from parent TabList */
  variant?: 'default' | 'pills' | 'underline';
  /** Full width passed from parent TabList */
  fullWidth?: boolean;
  /** Size passed from parent TabList */
  size?: 'sm' | 'md' | 'lg';
}

export interface TabPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Matching ID for the associated tab */
  tabId: string;
  /** Lazy load panel content only when active */
  lazy?: boolean;
  /** Index of the tab panel (for compatibility with some libraries) */
  index?: number;
}

export const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      defaultActiveTab,
      onTabChange,
      variant = 'default',
      dynamicHeight = false,
      position = 'top',
      fullWidth = false,
      size = 'md',
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const [activeTab, setActiveTabInternal] = useState<string>(defaultActiveTab || '');
    
    const setActiveTab = (tabId: string) => {
      setActiveTabInternal(tabId);
      if (onTabChange) {
        onTabChange(tabId);
      }
    };
    
    const hasVerticalPosition = position === 'left' || position === 'right';
    
    const positionClasses = {
      top: 'flex-col',
      bottom: 'flex-col-reverse',
      left: 'flex-row',
      right: 'flex-row-reverse',
    };
    
    return (
      <TabContext.Provider value={{ activeTab, setActiveTab }}>
        <div
          ref={ref}
          className={`
            flex ${positionClasses[position]}
            ${hasVerticalPosition ? 'items-start' : ''}
            ${dynamicHeight ? '' : 'h-full'}
            ${className}
          `}
          {...props}
        >
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child as React.ReactElement<any>, {
                position,
                variant,
                fullWidth,
                size,
              });
            }
            return child;
          })}
        </div>
      </TabContext.Provider>
    );
  }
);

Tabs.displayName = 'Tabs';

export const TabList = React.forwardRef<HTMLDivElement, TabListProps>(
  (
    {
      ariaKeyboardNav = true,
      className = '',
      children,
      position = 'top',
      variant = 'default',
      fullWidth = false,
      size = 'md',
      ...props
    },
    ref
  ) => {
    const hasVerticalPosition = position === 'left' || position === 'right';
    
    const variantClasses = {
      default: `
        border-b border-gray-200 dark:border-gray-700
        ${hasVerticalPosition ? 'border-b-0 border-r' : ''}
      `,
      pills: '',
      underline: '',
    };
    
    const positionClasses = {
      top: 'flex-row',
      bottom: 'flex-row',
      left: 'flex-col',
      right: 'flex-col',
    };
    
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!ariaKeyboardNav) return;
      
      // Navigation for arrow keys to be implemented
    };
    
    return (
      <div
        ref={ref}
        role="tablist"
        aria-orientation={hasVerticalPosition ? 'vertical' : 'horizontal'}
        className={`
          flex ${positionClasses[position]}
          ${hasVerticalPosition ? 'flex-shrink-0' : 'w-full'}
          ${variantClasses[variant]}
          ${className}
        `}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child as React.ReactElement<any>, {
              variant,
              fullWidth,
              size,
            });
          }
          return child;
        })}
      </div>
    );
  }
);

TabList.displayName = 'TabList';

export const Tab = React.forwardRef<HTMLButtonElement, TabProps>(
  (
    {
      tabId,
      disabled = false,
      icon,
      badge,
      className = '',
      children,
      variant = 'default',
      fullWidth = false,
      size = 'md',
      ...props
    },
    ref
  ) => {
    const context = useContext(TabContext);
    
    if (!context) {
      throw new Error('Tab must be used within a Tabs component');
    }
    
    const { activeTab, setActiveTab } = context;
    const isActive = activeTab === tabId;
    
    const sizeClasses = {
      sm: 'px-2 py-1 text-xs',
      md: 'px-4 py-2 text-sm',
      lg: 'px-6 py-3 text-base',
    };
    
    const variantClasses = {
      default: `
        border-b-2 
        ${isActive 
          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
          : 'border-transparent text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
        }
      `,
      pills: `
        rounded-full
        ${isActive 
          ? 'bg-blue-500 text-white'
          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
        }
      `,
      underline: `
        ${isActive 
          ? 'text-blue-600 after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-blue-500 dark:text-blue-400'
          : 'text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
        }
        relative
      `,
    };
    
    return (
      <button
        ref={ref}
        role="tab"
        aria-selected={isActive}
        aria-controls={`panel-${tabId}`}
        tabIndex={isActive ? 0 : -1}
        id={`tab-${tabId}`}
        disabled={disabled}
        className={`
          font-medium
          transition-colors duration-150
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50
          ${sizeClasses[size]}
          ${variantClasses[variant]}
          ${fullWidth ? 'flex-1 text-center' : ''}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${className}
        `}
        onClick={() => !disabled && setActiveTab(tabId)}
        {...props}
      >
        {icon && <span className="mr-2">{icon}</span>}
        {children}
        {badge && <span className="ml-2">{badge}</span>}
      </button>
    );
  }
);

Tab.displayName = 'Tab';

export const TabPanel = React.forwardRef<HTMLDivElement, TabPanelProps>(
  (
    {
      tabId,
      lazy = false,
      className = '',
      children,
      index, // We're not using this, but we accept it for compatibility
      ...props
    },
    ref
  ) => {
    const context = useContext(TabContext);
    
    if (!context) {
      throw new Error('TabPanel must be used within a Tabs component');
    }
    
    const { activeTab } = context;
    const isActive = activeTab === tabId;
    
    // If lazy loading is enabled and panel is not active, don't render content
    if (lazy && !isActive) {
      return null;
    }
    
    return (
      <div
        ref={ref}
        role="tabpanel"
        id={`panel-${tabId}`}
        aria-labelledby={`tab-${tabId}`}
        className={`
          ${isActive ? 'block' : 'hidden'}
          w-full
          p-4
          ${className}
        `}
        {...props}
      >
        {children}
      </div>
    );
  }
);

TabPanel.displayName = 'TabPanel';

// Export as a namespace object for convenient usage
const TabsNamespace = Object.assign(Tabs, {
  List: TabList,
  Tab: Tab,
  Panel: TabPanel,
});

export default TabsNamespace;